import { isAttachmentDescriptor, sanitizeAttachmentPolicy } from './attachments';
import { isRecord } from './config';
export const SUPPORA_PROTOCOL_NAMESPACE = 'suppora:web-channel';
export const SUPPORA_PROTOCOL_VERSION = 1;
export function createProtocolMessage(type, payload, id = createMessageId()) {
    return {
        namespace: SUPPORA_PROTOCOL_NAMESPACE,
        version: SUPPORA_PROTOCOL_VERSION,
        id,
        type,
        payload,
    };
}
export function isProtocolMessage(value) {
    if (!isRecord(value) ||
        value.namespace !== SUPPORA_PROTOCOL_NAMESPACE ||
        value.version !== SUPPORA_PROTOCOL_VERSION ||
        typeof value.id !== 'string' ||
        value.id.length === 0 ||
        typeof value.type !== 'string') {
        return false;
    }
    switch (value.type) {
        case 'host:init':
            return isHostInitPayload(value.payload);
        case 'host:command':
            return isHostCommandPayload(value.payload);
        case 'host:configure':
            return isRecord(value.payload) && isProtocolConfig(value.payload.config);
        case 'host:context':
            return isRecord(value.payload) && isPageContext(value.payload.page);
        case 'widget:ready':
            return (isRecord(value.payload) &&
                Array.isArray(value.payload.capabilities) &&
                value.payload.capabilities.every((capability) => typeof capability === 'string'));
        case 'widget:state':
            return (isRecord(value.payload) &&
                typeof value.payload.open === 'boolean' &&
                typeof value.payload.visible === 'boolean');
        case 'widget:event':
            return isWidgetEventPayload(value.payload);
        default:
            return false;
    }
}
export function isWidgetToHostMessage(value) {
    if (!isProtocolMessage(value)) {
        return false;
    }
    return (value.type === 'widget:ready' || value.type === 'widget:state' || value.type === 'widget:event');
}
export function isHostToWidgetMessage(value) {
    if (!isProtocolMessage(value)) {
        return false;
    }
    return (value.type === 'host:init' ||
        value.type === 'host:command' ||
        value.type === 'host:configure' ||
        value.type === 'host:context');
}
function isHostInitPayload(value) {
    if (!isRecord(value) || !isProtocolConfig(value.config) || !isRecord(value.state)) {
        return false;
    }
    const state = value.state;
    return (typeof state.open === 'boolean' &&
        typeof state.visible === 'boolean' &&
        isIdentityOrNull(state.identity) &&
        isMetadata(state.metadata) &&
        isPageContext(state.page));
}
function isHostCommandPayload(value) {
    if (!isRecord(value) || !isHostCommand(value.command)) {
        return false;
    }
    switch (value.command) {
        case 'identify':
            return (isRecord(value.payload) &&
                'identity' in value.payload &&
                isIdentityOrNull(value.payload.identity));
        case 'setMetadata':
            return isRecord(value.payload) && isMetadata(value.payload.metadata);
        default:
            return !('payload' in value) || value.payload === undefined;
    }
}
function isHostCommand(value) {
    return (value === 'open' ||
        value === 'close' ||
        value === 'toggle' ||
        value === 'show' ||
        value === 'hide' ||
        value === 'identify' ||
        value === 'logout' ||
        value === 'setMetadata');
}
function isWidgetEventPayload(value) {
    if (!isRecord(value) || typeof value.event !== 'string') {
        return false;
    }
    const payload = value.payload;
    switch (value.event) {
        case 'open':
        case 'close':
            return (isRecord(payload) &&
                (payload.source === 'api' || payload.source === 'launcher' || payload.source === 'widget'));
        case 'messageSent':
        case 'messageReceived':
            return (isRecord(payload) &&
                typeof payload.conversationId === 'string' &&
                typeof payload.messageId === 'string' &&
                typeof payload.text === 'string' &&
                Array.isArray(payload.attachments) &&
                payload.attachments.every(isAttachmentDescriptor));
        case 'error':
            return isErrorPayload(payload);
        default:
            return false;
    }
}
function isErrorPayload(value) {
    return (isRecord(value) &&
        typeof value.code === 'string' &&
        typeof value.message === 'string' &&
        typeof value.recoverable === 'boolean');
}
function isIdentityOrNull(value) {
    if (value === null) {
        return true;
    }
    if (!isRecord(value)) {
        return false;
    }
    for (const key of ['userId', 'email', 'name', 'avatarUrl', 'identityToken']) {
        if (key in value && typeof value[key] !== 'string') {
            return false;
        }
    }
    return (!('verificationStatus' in value) ||
        value.verificationStatus === 'anonymous' ||
        value.verificationStatus === 'unverified' ||
        value.verificationStatus === 'verified');
}
function isMetadata(value) {
    return (isRecord(value) &&
        Object.values(value).every((item) => typeof item === 'string' ||
            (typeof item === 'number' && Number.isFinite(item)) ||
            typeof item === 'boolean' ||
            item === null));
}
function isPageContext(value) {
    return (isRecord(value) &&
        typeof value.url === 'string' &&
        typeof value.title === 'string' &&
        typeof value.referrer === 'string');
}
function isProtocolConfig(value) {
    if (!isRecord(value)) {
        return false;
    }
    const stringKeys = [
        'channelId',
        'apiBaseUrl',
        'widgetUrl',
        'primaryColor',
        'onPrimaryColor',
        'backgroundColor',
        'textColor',
        'mutedTextColor',
        'borderColor',
        'launcherLabel',
        'title',
        'subtitle',
        'welcomeMessage',
        'companyName',
        'fontFamily',
        'privacyPolicyUrl',
    ];
    const booleanKeys = [
        'showLauncherLabel',
        'showLauncherIcon',
        'requireEmail',
        'collectName',
        'requireName',
        'initialOpen',
        'debug',
    ];
    if (!stringKeys.every((key) => typeof value[key] === 'string') ||
        !booleanKeys.every((key) => typeof value[key] === 'boolean') ||
        typeof value.borderRadius !== 'number' ||
        !Number.isFinite(value.borderRadius) ||
        typeof value.zIndex !== 'number' ||
        !Number.isFinite(value.zIndex) ||
        sanitizeAttachmentPolicy(value.attachments) === null) {
        return false;
    }
    return ((value.launcherPosition === 'bottom-left' || value.launcherPosition === 'bottom-right') &&
        (value.mode === 'light' || value.mode === 'dark' || value.mode === 'system') &&
        (value.identityMode === 'anonymous' ||
            value.identityMode === 'anonymous_or_unverified' ||
            value.identityMode === 'verified_only'));
}
function createMessageId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `suppora_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}
//# sourceMappingURL=protocol.js.map